"use client"

import { X, AlertTriangle, Crown, ArrowRight } from "lucide-react"
import { Modal } from "@/components/ui/modal"
import { Button } from "@/components/ui/button"
import { useRouter } from "next/navigation"
import { trackEvent } from "@/lib/mixpanel"

interface DeckLimitModalProps {
    isOpen: boolean
    onClose: () => void
    used?: number
    total?: number
    deckType?: string
}

export function DeckLimitModal({
    isOpen,
    onClose,
    used,
    total,
    deckType = "deck"
}: DeckLimitModalProps) {
    const router = useRouter()

    const handleUpgrade = () => {
        trackEvent("Upgrade Button Clicked", { source: "deck_limit_modal", deck_type: deckType })
        onClose()
        router.push("/pricing")
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="max-w-md p-0 overflow-hidden border-none bg-transparent shadow-2xl">
            <div className="bg-white rounded-2xl overflow-hidden">
                {/* Header */}
                <div className="relative px-6 pt-8 pb-6 bg-gradient-to-br from-amber-50 to-orange-50 border-b border-amber-100">
                    <button
                        type="button"
                        onClick={onClose}
                        className="absolute top-4 right-4 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-white/60 transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>

                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                            <AlertTriangle className="w-6 h-6 text-amber-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-900">Deck Limit Reached</h2>
                            <p className="text-sm text-slate-500 mt-0.5">
                                You&apos;ve used all available {deckType} units
                            </p>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="px-6 py-5">
                    {used != null && total != null && (
                        <div className="mb-5">
                            <div className="flex items-center justify-between text-sm mb-2">
                                <span className="text-slate-500">Usage</span>
                                <span className="font-semibold text-slate-700">{used} / {total} decks</span>
                            </div>
                            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-amber-400 to-red-400 rounded-full transition-all"
                                    style={{ width: "100%" }}
                                />
                            </div>
                        </div>
                    )}

                    <p className="text-sm text-slate-600 leading-relaxed">
                        Upgrade your plan to continue creating decks. Get access to more decks, premium features, and priority support.
                    </p>
                </div>

                {/* Actions */}
                <div className="px-6 pb-6 flex flex-col gap-2">
                    <Button
                        onClick={handleUpgrade}
                        className="w-full gap-2 bg-slate-900 hover:bg-slate-800 text-white py-5 rounded-xl font-semibold text-sm shadow-md"
                    >
                        <Crown className="w-4 h-4" />
                        Upgrade Now
                        <ArrowRight className="w-4 h-4" />
                    </Button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-full py-2.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                    >
                        Maybe later
                    </button>
                </div>
            </div>
        </Modal>
    )
}
